// ============================================================
//  src/pages/bots/tabs/ActivityTab.js
//
//  User Activity Feed — shows all users on the left, click one
//  to see their full conversation/log thread on the right,
//  plus their stored data (bot_storage_kv).
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getBotActivity, getBotUserActivity } from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';
import './ActivityTab.css';

// ── Helpers ───────────────────────────────────────────────────
function timeAgo(dateStr) {
    if (!dateStr) return '—';
    const d    = new Date(dateStr.replace(' ', 'T'));
    const secs = Math.floor((Date.now() - d) / 1000);
    if (secs < 60)    return 'just now';
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800)return `${Math.floor(secs / 86400)}d ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr.replace(' ', 'T'));
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr.replace(' ', 'T'));
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function initials(name) {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function levelDot(level) {
    const colors = { error: '#FF3B30', warn: '#FF9500', info: 'var(--primary)', debug: '#9AA0C0' };
    return colors[level] ?? '#9AA0C0';
}

// ── User Avatar ───────────────────────────────────────────────
function Avatar({ name, size = 36 }) {
    const colors = ['#002CED','#00C48C','#FF9500','#7C3AED','#0891B2','#BE185D'];
    const idx    = (name ?? '?').charCodeAt(0) % colors.length;
    return (
        <div className="act-avatar" style={{ width: size, height: size, minWidth: size, background: colors[idx] }}>
            {initials(name)}
        </div>
    );
}

// ── Left panel: user list item ────────────────────────────────
function UserRow({ user, selected, onClick }) {
    return (
        <div
            className={`act-user-row${selected ? ' selected' : ''}${user.uIsBlocked ? ' blocked' : ''}`}
            onClick={onClick}
        >
            <Avatar name={user.uName} />
            <div className="act-user-info">
                <div className="act-user-name">
                    {user.uName || user.uChatId}
                    {user.uIsBlocked ? <span className="act-blocked-badge">blocked</span> : null}
                </div>
                <div className="act-user-meta">
                    {user.uChatId}
                    {user.errorCount > 0 &&
                        <span className="act-error-pill">{user.errorCount} error{user.errorCount > 1 ? 's' : ''}</span>
                    }
                </div>
            </div>
            <div className="act-user-time">{timeAgo(user.uLastSeenAt)}</div>
        </div>
    );
}

// ── Right panel: activity thread ──────────────────────────────
function groupByDate(items) {
    const groups = {};
    items.forEach(item => {
        const key = formatDate(item.createdAt);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
    });
    return groups;
}

function ActivityThread({ bId, user, onClose }) {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab,     setTab]     = useState('activity'); // activity | storage
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBotUserActivity(bId, user.uChatId);
            if (res.data?.success) setData(res.data);
            else toast.error('Failed to load activity');
        } catch {
            toast.error('Failed to load activity');
        }
        setLoading(false);
    }, [bId, user.uChatId]);

    useEffect(() => { load(); }, [load]);

    // Scroll to bottom on load
    useEffect(() => {
        if (!loading) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [loading]);

    const grouped = data ? groupByDate(data.activity) : {};

    return (
        <div className="act-thread">
            {/* Thread header */}
            <div className="act-thread-header">
                <div className="act-thread-user">
                    <Avatar name={user.uName} size={32} />
                    <div>
                        <div className="act-thread-name">{user.uName || user.uChatId}</div>
                        <div className="act-thread-sub">
                            {user.uChatId}
                            {user.uPhone ? ` · ${user.uPhone}` : ''}
                            {' · Joined '}{timeAgo(user.uCreatedAt)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn btn-outline btn-sm" onClick={load}>↺</button>
                    <button className="act-close-btn" onClick={onClose}>✕</button>
                </div>
            </div>

            {/* Sub-tabs */}
            <div className="act-thread-tabs">
                <button
                    className={`act-thread-tab${tab === 'activity' ? ' active' : ''}`}
                    onClick={() => setTab('activity')}
                >
                    Activity {data ? `(${data.activity.length})` : ''}
                </button>
                <button
                    className={`act-thread-tab${tab === 'storage' ? ' active' : ''}`}
                    onClick={() => setTab('storage')}
                >
                    Stored Data {data?.storage?.length ? `(${data.storage.length})` : ''}
                </button>
            </div>

            {/* Thread body */}
            <div className="act-thread-body">
                {loading ? (
                    <div className="act-loading"><span className="spinner spinner-dark" /> Loading…</div>
                ) : tab === 'storage' ? (
                    /* Storage tab */
                    data?.storage?.length ? (
                        <div className="act-storage-list">
                            {data.storage.map(s => (
                                <div key={s.skKey} className="act-storage-row">
                                    <div className="act-storage-key">{s.skKey}</div>
                                    <div className="act-storage-val">{s.skValue}</div>
                                    <div className="act-storage-time">{timeAgo(s.skUpdatedAt)}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="act-empty-thread">No stored data for this user yet.</div>
                    )
                ) : (
                    /* Activity thread */
                    data?.activity?.length ? (
                        <div className="act-messages">
                            {Object.entries(grouped).map(([date, items]) => (
                                <div key={date}>
                                    <div className="act-date-divider">{date}</div>
                                    {items.map(item => (
                                        <ActivityEntry key={item.id} item={item} />
                                    ))}
                                </div>
                            ))}
                            <div ref={bottomRef} />
                        </div>
                    ) : (
                        <div className="act-empty-thread">
                            No activity logs yet for this user.<br />
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                Activity appears here once they interact with your bot.
                            </span>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

// ── Single activity entry (message bubble + bot response) ─────
function ActivityEntry({ item }) {
    const [expanded, setExpanded] = useState(false);
    const isError = item.level === 'error';
    const isWarn  = item.level === 'warn';

    return (
        <div className={`act-entry${isError ? ' act-entry--error' : ''}${isWarn ? ' act-entry--warn' : ''}`}>
            {/* User message bubble (if we have it) */}
            {item.userMsg && (
                <div className="act-bubble-row act-bubble-row--user">
                    <div className="act-bubble act-bubble--user">{item.userMsg}</div>
                    <div className="act-bubble-time">{formatTime(item.createdAt)}</div>
                </div>
            )}

            {/* Bot log entry */}
            <div className="act-bubble-row act-bubble-row--bot">
                <div
                    className="act-bubble act-bubble--bot"
                    onClick={() => setExpanded(e => !e)}
                    title="Click to expand"
                >
                    <span
                        className="act-level-dot"
                        style={{ background: levelDot(item.level) }}
                        title={item.level}
                    />
                    <span className="act-bot-msg">{item.message}</span>
                </div>
                {!item.userMsg && (
                    <div className="act-bubble-time">{formatTime(item.createdAt)}</div>
                )}
            </div>
        </div>
    );
}

// ── Search box ────────────────────────────────────────────────
function SearchBox({ value, onChange }) {
    return (
        <div className="act-search">
            <span className="act-search-icon">⌕</span>
            <input
                className="act-search-input"
                placeholder="Search by name or phone…"
                value={value}
                onChange={e => onChange(e.target.value)}
            />
            {value && (
                <button className="act-search-clear" onClick={() => onChange('')}>✕</button>
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────
export default function ActivityTab({ bId }) {
    const [users,    setUsers]    = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState('');
    const [selected, setSelected] = useState(null);
    const [total,    setTotal]    = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBotActivity(bId);
            if (res.data?.success) {
                setUsers(res.data.users ?? []);
                setTotal(res.data.total ?? 0);
            }
        } catch {
            toast.error('Failed to load activity');
        }
        setLoading(false);
    }, [bId]);

    useEffect(() => { load(); }, [load]);

    const filtered = users.filter(u => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (u.uName    ?? '').toLowerCase().includes(q) ||
            (u.uChatId  ?? '').toLowerCase().includes(q) ||
            (u.uPhone   ?? '').toLowerCase().includes(q)
        );
    });

    return (
        <div className="act-root">
            {/* ── Left panel ── */}
            <div className="act-left">
                <div className="act-left-header">
                    <div>
                        <div className="tab-title">Activity Feed</div>
                        <div className="tab-sub">{total} user{total !== 1 ? 's' : ''}</div>
                    </div>
                    <button className="btn btn-outline btn-sm" onClick={load}>↺</button>
                </div>

                <SearchBox value={search} onChange={setSearch} />

                {loading ? (
                    <div className="act-loading"><span className="spinner spinner-dark" /> Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="tab-empty" style={{ padding: '24px 16px' }}>
                        {search ? 'No users match your search.' : 'No users yet. Activity appears once users message your bot.'}
                    </div>
                ) : (
                    <div className="act-user-list">
                        {filtered.map(u => (
                            <UserRow
                                key={u.uId}
                                user={u}
                                selected={selected?.uId === u.uId}
                                onClick={() => setSelected(u)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── Right panel ── */}
            <div className="act-right">
                {selected ? (
                    <ActivityThread
                        bId={bId}
                        user={selected}
                        onClose={() => setSelected(null)}
                    />
                ) : (
                    <div className="act-empty-state">
                        <div className="act-empty-icon">💬</div>
                        <div className="act-empty-title">Select a user</div>
                        <div className="act-empty-sub">
                            Click any user on the left to view their full activity thread and stored data.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}